import React, { useState, useEffect, useRef, useMemo } from "react"
import QrScanner from "qr-scanner"
import { Container, Card, CardBody, Row, Col, Input, Button } from "reactstrap"
import { useLocation, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import axios from "axios"
import { debounce } from "lodash"
import AttendanceTabs from "./Tabs"
import Present from "../Report/Present"
import "react-toastify/dist/ReactToastify.css"
import Spinners from "components/Common/Spinner"
import Pickup from "./Pickup"

const QRScanner = () => {
  const acad_id = localStorage.getItem("acad_id")
  const live_Url = "https://marcopolo.prisms.in"
  const { sid, staff_id } = useParams()
  const location = useLocation()
  const videoRef = useRef(null)
  const inputRef = useRef(null)

  const lastScanTimeRef = useRef(0)
  const queryParams = new URLSearchParams(location.search)
  const attendType = queryParams.get("attendType")
  const [rfid, setRfid] = useState()

  const [studData, setStudentData] = useState()
  const [activeTab, setActiveTab] = useState(1)
  const [pickUp, setPickup] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState("")

  const fixAndParseJSON = rawData => {
    if (!rawData || typeof rawData !== "string") {
      console.error("Invalid QR code data:", rawData)
      return null
    }

    let fixedData = rawData
      .replace(/(\w+):/g, '"$1":')
      .replace(/:\s*([^",}\]]+)/g, (match, value) => {
        if (
          /^\d+(\.\d+)?$/.test(value) ||
          ["true", "false", "null"].includes(value)
        ) {
          return `:${value}`
        } else {
          return `:"${value.replace(/"/g, '\\"')}"`
        }
      })

    try {
      const parsedData = JSON.parse(fixedData)
      return parsedData
    } catch (err) {
      console.error("❌ JSON Parse Error:", err)
      return null
    }
  }

  const handleCheckTransport = async userid => {
    try {
      const res = await axios.post(
        `https://marcopolo.prisms.in/api/QR/attendance/transportDetails`,
        {
          sid,
          user_id: userid,
        }
      )
      return res.data?.data || null
    } catch (err) {
      toast.warning("Transport not found")
      return null
    }
  }

  const handleScan = async parsedResult => {
    if (!Array.isArray(parsedResult) || !parsedResult[0]?.userid) {
      toast.error("Invalid QR data format")
      return
    }

    const studentId = parsedResult[0].userid

    const transport = await handleCheckTransport(studentId)

    if (!transport || !Array.isArray(transport) || transport.length === 0) {
      toast.warning("This student does not have transport assigned.")
      return
    }

    const hasTransport =
      attendType === "pickup"
        ? transport[0].pickup_route_name &&
          transport[0].pickup_route_name !== "NA"
        : transport[0].drop_route_name && transport[0].drop_route_name !== "NA"

    if (!hasTransport) {
      toast.warning(
        `This student does not have ${attendType} transport facility.`
      )
      return
    }

    try {
      const res = await axios.post(
        `${live_Url}/api/QR/attendance/bus/pickup/students`,
        {
          sid,
          staff_id,
          stud_id: studentId,
          acad_year: acad_id,
          event: attendType,
          bus_id:
            attendType === "pickup"
              ? transport[0].pickup_bus_id
              : transport[0].drop_bus_id,
          route_id:
            attendType === "pickup"
              ? transport[0].pickup_route_id
              : transport[0].drop_route_id,
          pickup_stop_id: transport[0].pickup_stop_id,
          drop_stop_id: transport[0].drop_stop_id,
        }
      )

      toast.success(res.data.message || "Attendance marked successfully")

      if (res.data.message === "Attendance marked") {
        setPickup(prev => !prev)
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Something went wrong!"
      toast.error(msg)
    }
  }

  useEffect(() => {
    let scanner
    const scanCooldown = 3000

    const startScanner = async () => {
      setIsLoading(true)
      try {
        if (videoRef.current) {
          scanner = new QrScanner(
            videoRef.current,
            result => {
              const now = Date.now()
              if (now - lastScanTimeRef.current < scanCooldown) return

              const parsed = fixAndParseJSON(result.data)
              if (parsed) {
                lastScanTimeRef.current = now
                handleScan(parsed)
              } else {
                setErrorMessage("QR code is invalid")
              }
            },
            { returnDetailedScanResult: true }
          )

          await scanner.start()
          console.log("📸 Scanner started")
        } else {
          console.error("No video ref available")
        }
      } catch (error) {
        console.error("Error starting scanner:", error)
        setErrorMessage("Camera not accessible or permission denied")
      } finally {
        setIsLoading(false)
      }
    }

    startScanner()

    return () => {
      if (scanner) {
        scanner.stop()
      }
    }
  }, [sid, staff_id])

  useEffect(() => {
    axios
      .post(`${live_Url}/api/QR/attendance/bus/route/students`, {
        sid,
        staff_id,
        attendType,
      })
      .then(res => setStudentData(res.data))
      .catch(err => console.error(err))
  }, [pickUp])

  const handleSubmitRFID = async () => {
    if (!rfid && rfid.length > 0) {
      toast.error("Please enter RFID")
      return
    }

    try {
      const res = await axios.post(
        `${live_Url}/api/QR/attendance/userDetailsFromRFID`,
        {
          sid,
          rfid_id: rfid,
        }
      )

      const userData = res?.data?.data

      if (!userData || !Array.isArray(userData) || !userData[0]?.user_id) {
        toast.error("Invalid RFID data")
        return
      }

      const studentId = userData[0].user_id

      // Step 2: Get transport details
      const transport = await handleCheckTransport(studentId)

      if (!transport || !Array.isArray(transport) || transport.length === 0) {
        toast.warning("This student does not have transport assigned.")
        return
      }

      const hasTransport =
        attendType === "pickup"
          ? transport[0].pickup_route_name &&
            transport[0].pickup_route_name !== "NA"
          : transport[0].drop_route_name &&
            transport[0].drop_route_name !== "NA"

      if (!hasTransport) {
        toast.warning(
          `This student does not have ${attendType} transport facility.`
        )
        return
      }

      // Step 3: Mark attendance
      try {
        const markRes = await axios.post(
          `${live_Url}/api/QR/attendance/bus/pickup/students`,
          {
            sid,
            staff_id,
            stud_id: studentId,
            acad_year: acad_id,
            event: attendType,
            bus_id:
              attendType === "pickup"
                ? transport[0].pickup_bus_id
                : transport[0].drop_bus_id,
            route_id:
              attendType === "pickup"
                ? transport[0].pickup_route_id
                : transport[0].drop_route_id,
            pickup_stop_id: transport[0].pickup_stop_id,
            drop_stop_id: transport[0].drop_stop_id,
          }
        )

        toast.success(markRes.data.message || "Attendance marked successfully")

        if (markRes.data.message === "Attendance marked") {
          setPickup(prev => !prev)
          setRfid("")
        }
      } catch (err) {
        const msg = err?.response?.data?.message || "Something went wrong!"
        toast.error(msg)
        setRfid("")
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "RFID fetch failed"
      toast.error(msg)
      setRfid("")
    }
  }

  const debouncedSubmit = useMemo(
    () =>
      debounce(value => {
        if (value && value.length > 0) {
          handleSubmitRFID(value)
        }
      }, 500),
    [handleSubmitRFID]
  )

  useEffect(() => {
    debouncedSubmit(rfid)

    return () => {
      debouncedSubmit.cancel()
    }
  }, [rfid])

  return (
    <div style={{ height: "100vh" }}>
      <Container style={{ backgroundColor: "#211689", height: "100%" }}>
        <Row>
          <Col xs={12} sm={8} md={6} lg={5}>
            <Card className="p-0">
              <CardBody style={{ backgroundColor: "#211689" }}>
                <Col className="d-flex justify-content-between align-items-center mb-3"></Col>
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "220px" }}
                >
                  <video
                    ref={videoRef}
                    style={{
                      width: "250px",
                      height: "250px",
                      objectFit: "cover",
                    }}
                    muted
                    playsInline
                  />
                </div>

                <Input
                  type="number"
                  value={rfid}
                  onChange={e => {
                    const rawValue = e.target.value

                    if (rawValue === "" || /^[1-9]\d{0,9}$/.test(rawValue)) {
                      setRfid(rawValue)
                    }
                  }}
                  placeholder="Enter up to 10-digit RFID (no leading zero)"
                  className="mt-3"
                />

                {/* <div className="d-flex justify-content-center">
                  <Button
                    color="dark"
                    className="mt-2"
                    onClick={handleSubmitRFID}
                  >
                    Submit
                  </Button>
                </div> */}

                {isLoading && <Spinners />}
                {errorMessage && (
                  <p className="text-danger text-center">{errorMessage}</p>
                )}

                <Col className="d-flex justify-content-between align-items-center mt-3">
                  <h6 className="m-0 text-white">
                    Total - {studData?.Report || 0}
                  </h6>
                  <h6 className="m-0 text-white">
                    {attendType === "pickup" ? "Pick Up" : "Drop"} -{" "}
                    {studData?.Report || 0}
                  </h6>
                </Col>

                <Col>
                  <AttendanceTabs
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    attendType={attendType}
                  />
                </Col>
                <Col>
                  {activeTab ? (
                    <Present studData={studData} />
                  ) : (
                    <Pickup attendType={attendType} />
                  )}
                </Col>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default QRScanner
